/**
 * Tab-visibility gating for periodic work.
 *
 * The SPA had zero visibilitychange handling: every poll (dashboard 5s,
 * zapd-engine 5s, ops-rate 2s, statusbar 2s, dedup 30s) kept firing in a
 * background tab. `visibleInterval` is a drop-in setInterval replacement
 * that pauses while the tab is hidden and fires once immediately when the
 * user returns (so stale views refresh right away instead of waiting out
 * a full period).
 *
 * SSE connections are deliberately NOT gated here — tearing them down on
 * tab-switch would trade idle wakeups for reconnect churn + missed events.
 */

/**
 * @param {() => void} fn   callback (exceptions are the caller's problem,
 *                           same as with a raw setInterval)
 * @param {number} ms       period in milliseconds
 * @param {{immediate?: boolean}} [opts]  immediate — also fire once right
 *                           away when armed (default false, like setInterval)
 * @returns {() => void} cancel
 */
export function visibleInterval(fn, ms, opts) {
  let handle = 0;

  const arm = () => {
    if (handle) return;
    handle = setInterval(fn, ms);
  };
  const disarm = () => {
    if (!handle) return;
    clearInterval(handle);
    handle = 0;
  };

  const onVisibility = () => {
    if (document.hidden) {
      disarm();
    } else {
      fn(); // catch up immediately on return
      arm();
    }
  };

  document.addEventListener('visibilitychange', onVisibility);
  if (!document.hidden) {
    if (opts && opts.immediate) fn();
    arm();
  }

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    disarm();
  };
}
